import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateNIS2FinalReport,
  useNIS2FinalReport,
  useNIS2FinalReportAutoPopulate,
  useSubmitNIS2FinalReport,
  useUpdateNIS2FinalReport,
  type CreateNIS2FinalReportRequest,
  type NIS2NotificationResponse,
} from '@/features/incidents/api/nis2FinalReport';
import { CirtEmailSubmissionDialog } from '@/features/incidents/components/CirtEmailSubmissionDialog';
import {
  buildCirtAttachmentFilename,
  downloadTextAsFile,
  openCirtMailDraft,
} from '@/features/incidents/utils/cirt-email';

export const Route = createFileRoute('/_authenticated/nis2/incidents/$incidentId/final-report')({
  beforeLoad: () => ({
    getTitle: () => 'NIS2 Final Report (30d)',
  }),
  component: FinalReportRoute,
});

interface FinalReportFormState {
  authorityName: string;
  contactEmail: string;
  incidentSummary: string;
  detailedDescription: string;
  severityAssessment: string;
  impactDescription: string;
  affectedServices: string;
  affectedUsersCount: string;
  affectedCountries: string;
  crossBorderImpact: boolean;
  crossBorderDetails: string;
  rootCauseAnalysis: string;
  rootCauseFactors: string;
  mitigationMeasuresTaken: string;
  mitigationMeasuresList: string;
  preventiveMeasures: string;
  lessonsLearned: string;
  followUpActions: string;
  incidentResolved: boolean;
  resolutionDate: string;
  additionalNotes: string;
}

const EMPTY_FORM_STATE: FinalReportFormState = {
  authorityName: 'CIRT',
  contactEmail: '',
  incidentSummary: '',
  detailedDescription: '',
  severityAssessment: '',
  impactDescription: '',
  affectedServices: '',
  affectedUsersCount: '',
  affectedCountries: '',
  crossBorderImpact: false,
  crossBorderDetails: '',
  rootCauseAnalysis: '',
  rootCauseFactors: '',
  mitigationMeasuresTaken: '',
  mitigationMeasuresList: '',
  preventiveMeasures: '',
  lessonsLearned: '',
  followUpActions: '',
  incidentResolved: true,
  resolutionDate: '',
  additionalNotes: '',
};

function splitList(value: string) {
  return value
    .split(/\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function formatDateTimeLocal(value: string | null | undefined) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');

  return (
    [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())].join('-') +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function buildRequest(form: FinalReportFormState): CreateNIS2FinalReportRequest | null {
  const requiredFields: Array<[keyof FinalReportFormState, string]> = [
    ['authorityName', 'Authority name'],
    ['incidentSummary', 'Incident summary'],
    ['detailedDescription', 'Detailed description'],
    ['severityAssessment', 'Severity assessment'],
    ['impactDescription', 'Impact description'],
    ['affectedServices', 'Affected services'],
    ['rootCauseAnalysis', 'Root cause analysis'],
    ['mitigationMeasuresTaken', 'Mitigation measures taken'],
    ['preventiveMeasures', 'Preventive measures'],
    ['followUpActions', 'Follow-up actions'],
  ];

  for (const [field, label] of requiredFields) {
    if (!form[field].trim()) {
      toast.error(`${label} is required.`);
      return null;
    }
  }

  return {
    authorityName: form.authorityName.trim(),
    contactEmail: form.contactEmail.trim() || null,
    incidentSummary: form.incidentSummary.trim(),
    detailedDescription: form.detailedDescription.trim(),
    severityAssessment: form.severityAssessment.trim(),
    impactDescription: form.impactDescription.trim(),
    affectedServices: form.affectedServices.trim(),
    affectedUsersCount: form.affectedUsersCount ? Number(form.affectedUsersCount) : null,
    affectedCountries: splitList(form.affectedCountries),
    crossBorderImpact: form.crossBorderImpact,
    crossBorderDetails: form.crossBorderDetails.trim() || null,
    rootCauseAnalysis: form.rootCauseAnalysis.trim(),
    rootCauseFactors: splitList(form.rootCauseFactors),
    mitigationMeasuresTaken: form.mitigationMeasuresTaken.trim(),
    mitigationMeasuresList: splitList(form.mitigationMeasuresList),
    preventiveMeasures: form.preventiveMeasures.trim(),
    lessonsLearned: form.lessonsLearned.trim() || null,
    followUpActions: form.followUpActions.trim(),
    incidentResolved: form.incidentResolved,
    resolutionDate: form.resolutionDate ? new Date(form.resolutionDate).toISOString() : null,
    additionalNotes: form.additionalNotes.trim() || null,
  };
}

function FinalReportRoute() {
  const { t } = useTranslation('common');
  const { incidentId } = Route.useParams();
  const navigate = useNavigate();
  const [form, setForm] = React.useState<FinalReportFormState>(EMPTY_FORM_STATE);
  const [initialized, setInitialized] = React.useState(false);
  const [isSubmissionDialogOpen, setIsSubmissionDialogOpen] = React.useState(false);

  const reportQuery = useNIS2FinalReport({ incidentId });
  const autoPopulateQuery = useNIS2FinalReportAutoPopulate({ incidentId });
  const createMutation = useCreateNIS2FinalReport({ incidentId });
  const updateMutation = useUpdateNIS2FinalReport({ incidentId });
  const submitMutation = useSubmitNIS2FinalReport({ incidentId });

  React.useEffect(() => {
    if (initialized || !autoPopulateQuery.data) {
      return;
    }

    setForm((current) => ({
      ...current,
      incidentSummary: autoPopulateQuery.data.incidentSummary ?? current.incidentSummary,
      detailedDescription:
        autoPopulateQuery.data.detailedDescription ?? current.detailedDescription,
      severityAssessment: autoPopulateQuery.data.severityAssessment ?? current.severityAssessment,
      impactDescription: autoPopulateQuery.data.impactDescription ?? current.impactDescription,
      affectedServices: autoPopulateQuery.data.affectedServices ?? current.affectedServices,
      affectedUsersCount: autoPopulateQuery.data.affectedUsersCount
        ? String(autoPopulateQuery.data.affectedUsersCount)
        : current.affectedUsersCount,
      rootCauseAnalysis: autoPopulateQuery.data.rootCauseAnalysis ?? current.rootCauseAnalysis,
      rootCauseFactors:
        autoPopulateQuery.data.rootCauseFactors?.join('\n') ?? current.rootCauseFactors,
      mitigationMeasuresTaken:
        autoPopulateQuery.data.mitigationMeasuresTaken ?? current.mitigationMeasuresTaken,
      mitigationMeasuresList:
        autoPopulateQuery.data.mitigationMeasuresList?.join('\n') ?? current.mitigationMeasuresList,
      preventiveMeasures: autoPopulateQuery.data.preventiveMeasures ?? current.preventiveMeasures,
      lessonsLearned: autoPopulateQuery.data.lessonsLearned ?? current.lessonsLearned,
      followUpActions: autoPopulateQuery.data.followUpActions ?? current.followUpActions,
      resolutionDate:
        formatDateTimeLocal(autoPopulateQuery.data.resolutionDate) || current.resolutionDate,
    }));
    setInitialized(true);
  }, [autoPopulateQuery.data, initialized]);

  const report = reportQuery.data;
  const isSubmitted = report?.status === 'submitted' || report?.status === 'acknowledged';
  const isLoading = reportQuery.isLoading || autoPopulateQuery.isLoading;
  const isPersisting = createMutation.isPending || updateMutation.isPending;

  const updateField = <K extends keyof FinalReportFormState>(
    field: K,
    value: FinalReportFormState[K]
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const persistReport = async (): Promise<NIS2NotificationResponse | null> => {
    const request = buildRequest(form);
    if (!request) {
      return null;
    }

    try {
      if (report) {
        const updated = await updateMutation.mutateAsync(request);
        toast.success('Final report draft updated.');
        return updated;
      }

      const created = await createMutation.mutateAsync(request);
      toast.success('Final report draft created.');
      return created;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save final report.');
      return null;
    }
  };

  const handleSubmit = async () => {
    const savedReport = await persistReport();
    if (!savedReport) {
      return;
    }

    try {
      const attachmentName = buildCirtAttachmentFilename('30 day', incidentId, 'txt');
      downloadTextAsFile(savedReport.content, attachmentName);
      openCirtMailDraft({
        formType: '30 day',
        incidentLabel: incidentId,
        attachmentName,
      });
      await submitMutation.mutateAsync({
        content: savedReport.content,
        contactEmail: form.contactEmail.trim() || null,
      });
      toast.success('Final report marked as submitted.');
      setIsSubmissionDialogOpen(false);
      void reportQuery.refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit final report.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (reportQuery.error && !reportQuery.error.message.includes('404')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-5xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.unableToLoadFinalReport')}</AlertTitle>
            <AlertDescription>{reportQuery.error.message}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <Button
          variant="ghost"
          onClick={() => {
            void navigate({ to: '/incidents/$incidentId', params: { incidentId } });
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to incident
        </Button>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold">NIS2 Final Report</h1>
            <p className="mt-2 text-muted-foreground">
              30-day submission form for CIRT. Auto-populated fields can be edited before saving or
              submitting the report.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant={isSubmitted ? 'secondary' : 'outline'}>
              {report?.status ?? 'draft'}
            </Badge>
            {report?.deadline ? (
              <Badge variant="outline">Deadline {new Date(report.deadline).toLocaleString()}</Badge>
            ) : null}
          </div>
        </div>

        {autoPopulateQuery.error ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Auto-populate unavailable</AlertTitle>
            <AlertDescription>{autoPopulateQuery.error.message}</AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Final report form</CardTitle>
            <CardDescription>
              Fill the mandatory NIS2 report details and submit to the appropriate CIRT workflow.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="authorityName">Authority</Label>
                <Input
                  id="authorityName"
                  value={form.authorityName}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('authorityName', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={form.contactEmail}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('contactEmail', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="incidentSummary">Incident summary</Label>
                <Textarea
                  id="incidentSummary"
                  rows={4}
                  value={form.incidentSummary}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('incidentSummary', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="detailedDescription">Detailed description</Label>
                <Textarea
                  id="detailedDescription"
                  rows={4}
                  value={form.detailedDescription}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('detailedDescription', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="severityAssessment">Severity assessment</Label>
                <Textarea
                  id="severityAssessment"
                  rows={4}
                  value={form.severityAssessment}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('severityAssessment', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="impactDescription">Impact description</Label>
                <Textarea
                  id="impactDescription"
                  rows={4}
                  value={form.impactDescription}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('impactDescription', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="affectedServices">Affected services</Label>
                <Textarea
                  id="affectedServices"
                  rows={3}
                  value={form.affectedServices}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('affectedServices', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="affectedUsersCount">Affected users count</Label>
                <Input
                  id="affectedUsersCount"
                  type="number"
                  min="0"
                  value={form.affectedUsersCount}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('affectedUsersCount', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="rootCauseAnalysis">Root cause analysis</Label>
                <Textarea
                  id="rootCauseAnalysis"
                  rows={4}
                  value={form.rootCauseAnalysis}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('rootCauseAnalysis', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rootCauseFactors">Root cause factors</Label>
                <Textarea
                  id="rootCauseFactors"
                  rows={4}
                  placeholder="One factor per line"
                  value={form.rootCauseFactors}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('rootCauseFactors', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="mitigationMeasuresTaken">Mitigation measures taken</Label>
                <Textarea
                  id="mitigationMeasuresTaken"
                  rows={4}
                  value={form.mitigationMeasuresTaken}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('mitigationMeasuresTaken', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mitigationMeasuresList">Mitigation checklist</Label>
                <Textarea
                  id="mitigationMeasuresList"
                  rows={4}
                  placeholder="One item per line"
                  value={form.mitigationMeasuresList}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('mitigationMeasuresList', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="preventiveMeasures">Preventive measures</Label>
                <Textarea
                  id="preventiveMeasures"
                  rows={4}
                  value={form.preventiveMeasures}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('preventiveMeasures', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="followUpActions">Follow-up actions</Label>
                <Textarea
                  id="followUpActions"
                  rows={4}
                  value={form.followUpActions}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('followUpActions', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="affectedCountries">Affected countries</Label>
                <Textarea
                  id="affectedCountries"
                  rows={3}
                  placeholder="Comma or newline separated"
                  value={form.affectedCountries}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('affectedCountries', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="crossBorderDetails">Cross-border details</Label>
                <Textarea
                  id="crossBorderDetails"
                  rows={3}
                  value={form.crossBorderDetails}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('crossBorderDetails', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lessonsLearned">Lessons learned</Label>
                <Textarea
                  id="lessonsLearned"
                  rows={3}
                  value={form.lessonsLearned}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('lessonsLearned', event.target.value);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="additionalNotes">Additional notes</Label>
                <Textarea
                  id="additionalNotes"
                  rows={3}
                  value={form.additionalNotes}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('additionalNotes', event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="resolutionDate">Resolution date</Label>
                <Input
                  id="resolutionDate"
                  type="datetime-local"
                  value={form.resolutionDate}
                  disabled={isSubmitted}
                  onChange={(event) => {
                    updateField('resolutionDate', event.target.value);
                  }}
                />
              </div>
              <div className="flex flex-col gap-4 rounded-lg border p-4">
                <label className="flex items-center gap-3">
                  <Checkbox
                    checked={form.crossBorderImpact}
                    disabled={isSubmitted}
                    onCheckedChange={(checked) => {
                      updateField('crossBorderImpact', checked === true);
                    }}
                  />
                  <span className="text-sm font-medium">Cross-border impact</span>
                </label>
                <label className="flex items-center gap-3">
                  <Checkbox
                    checked={form.incidentResolved}
                    disabled={isSubmitted}
                    onCheckedChange={(checked) => {
                      updateField('incidentResolved', checked === true);
                    }}
                  />
                  <span className="text-sm font-medium">Incident resolved</span>
                </label>
              </div>
            </div>

            {!isSubmitted ? (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={isPersisting || submitMutation.isPending}
                  onClick={() => {
                    void persistReport();
                  }}
                >
                  {isPersisting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save draft
                </Button>
                <Button
                  disabled={isPersisting || submitMutation.isPending}
                  onClick={() => {
                    setIsSubmissionDialogOpen(true);
                  }}
                >
                  {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit to CIRT
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {report?.content ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('common:routeErrors.savedReportContent')}</CardTitle>
              <CardDescription>
                Backend-generated final report draft or submitted content.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap rounded-lg bg-muted p-4 text-sm">
                {report.content}
              </pre>
            </CardContent>
          </Card>
        ) : null}

        <CirtEmailSubmissionDialog
          open={isSubmissionDialogOpen}
          onOpenChange={setIsSubmissionDialogOpen}
          formType="30 day"
          incidentLabel={incidentId}
          attachmentName={buildCirtAttachmentFilename('30 day', incidentId, 'txt')}
          isSubmitting={isPersisting || submitMutation.isPending}
          onSubmit={() => {
            void handleSubmit();
          }}
        />
      </div>
    </div>
  );
}
